import { styled } from "goober";
import { useState } from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { sortPersonalFirst } from "../lib/sortSpaces";
import { spaceIcon, spaceIconTooltip } from "../lib/spaceIcon";
import { DiscoverableSpaceIcon } from "./DiscoverableSpaceIcon";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { JoinSpaceModal } from "./JoinSpaceModal";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const PillOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: var(--alpha4);
  border-radius: 12px;
  cursor: pointer;
  color: var(--text-secondary);
  &:hover {
    background: var(--alpha8);
  }
`;

const FieldOpener = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background: var(--alpha4);
  }
`;

const FieldValue = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

type Props = {
  selectedSpaceId: string | null;
  onSelect: (spaceId: string) => void;
  membersOnly?: boolean;
  variant?: "pill" | "field";
};

export function SpaceDropdown({
  selectedSpaceId,
  onSelect,
  membersOnly,
  variant = "pill",
}: Props) {
  const displayName = useSpaceDisplayName();
  const [spaces] = useSpaces({ settingsView: false });
  const [personalSpace] = useQuery(queries.personalSpace());
  const personalSpaceId = personalSpace?.id ?? null;
  const memberSpaces = spaces.filter(isSpaceMember);
  const discoverableSpaces = spaces.filter((s) => !isSpaceMember(s));
  const sortedMembers = sortPersonalFirst(memberSpaces, personalSpaceId);
  const selectedSpace = memberSpaces.find((s) => s.id === selectedSpaceId);
  const [joiningSpace, setJoiningSpace] = useState<
    (typeof discoverableSpaces)[number] | null
  >(null);
  const selectedLabel = selectedSpace
    ? displayName(selectedSpace)
    : "Select space";
  const selectedTooltip = selectedSpace
    ? spaceIconTooltip(selectedSpace.mode)
    : undefined;
  const openerIcon = (
    <Icon
      name={selectedSpace ? spaceIcon(selectedSpace.mode) : "hash"}
      size={10}
      color="var(--text-secondary)"
    />
  );
  const fieldIcon = (
    <Icon
      name={selectedSpace ? spaceIcon(selectedSpace.mode) : "hash"}
      size={16}
      color={selectedSpace ? "var(--text-primary)" : "var(--text-secondary)"}
    />
  );

  const sections = [
    {
      items: sortedMembers.map((space) => ({
        type: "button" as const,
        key: space.id,
        left: <Icon name={spaceIcon(space.mode)} size={16} />,
        label: displayName(space),
        onClick: () => onSelect(space.id),
      })),
    },
  ];

  if (!membersOnly && discoverableSpaces.length > 0) {
    sections.push({
      items: discoverableSpaces
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((space) => ({
          type: "button" as const,
          key: space.id,
          left: <DiscoverableSpaceIcon />,
          label: displayName(space),
          muted: true,
          onClick: () => setJoiningSpace(space),
          keepOpen: true,
        })),
    });
  }

  return (
    <>
      <Dropdown
        align={variant === "field" ? "bottom-left" : "bottom"}
        opener={
          variant === "field" ? (
            <FieldOpener>
              <FieldValue>
                {selectedTooltip ? (
                  <Tooltip content={selectedTooltip}>{fieldIcon}</Tooltip>
                ) : (
                  fieldIcon
                )}
                <Text
                  variant="text-regular"
                  ellipsis
                  color={
                    selectedSpace
                      ? "var(--text-primary)"
                      : "var(--text-secondary)"
                  }
                >
                  {selectedLabel}
                </Text>
              </FieldValue>
              <Icon
                name="chevron-down"
                size={12}
                color="var(--text-secondary)"
              />
            </FieldOpener>
          ) : (
            <PillOpener>
              {selectedTooltip ? (
                <Tooltip content={selectedTooltip}>{openerIcon}</Tooltip>
              ) : (
                openerIcon
              )}
              <Text variant="text-small-md" ellipsis style={{ maxWidth: 160 }}>
                {selectedLabel}
              </Text>
              <Icon
                name="chevron-down"
                size={10}
                color="var(--text-secondary)"
              />
            </PillOpener>
          )
        }
        sections={sections}
      />
      <JoinSpaceModal
        open={joiningSpace !== null}
        onClose={() => setJoiningSpace(null)}
        space={joiningSpace}
      />
    </>
  );
}
